import Icon from 'components/Icon';
import * as consts from 'consts/consts';
import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Button } from 'reactstrap';
import { type Person } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import CustomSurveyResponsesViewer, {
  type SurveyParticipation,
} from 'views/CustomSurveys/CustomSurveyResponsesViewer';
import Page from 'views/Layout/Pages/Page';
import AttachedContentTypeCommentsProvider from 'views/Widgets/Comments/AttachedContentTypeCommentsProvider';

interface RouteParams {
  id: string;
  instanceId: string;
  personId: string;
}

interface LocationState {
  person?: Person;
}

const CustomSurveyParticipantResponsePage: FC = () => {
  const { id, instanceId, personId } = useParams<RouteParams>();
  const { formatMessage } = useIntl();
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();

  const { data: participation } = useConfirmApi<SurveyParticipation>({
    method: 'GET',
    url: `custom-survey/instances/${instanceId}/admin-participation/${personId}`,
  });

  const answerIds = participation?.questions.map((q) => q.answer?.id) ?? [];

  const handleBack = useCallback(() => {
    history.push(
      `${consts.ADMINISTRATION_CUSTOM_SURVEYS_RESPONSES(formatMessage).path.replace(':id', id)}?instance=${instanceId}`
    );
  }, [history, formatMessage, id, instanceId]);

  return (
    <Page plainTextTitle={participation?.survey_name ?? ''}>
      <>
        <div className="position-relative d-flex align-items-center mt-4 mb-4">
          <Button color="link" onClick={handleBack}>
            <Icon className="me-1" icon={consts.ICONS.ARROW_LEFT} />
            <FormattedMessage
              id="app.views.administration.custom_surveys.participant_response.back"
              defaultMessage="Back"
            />
          </Button>
          <h2 className="mb-0 position-absolute start-50 translate-middle-x">
            {participation?.survey_name ?? ''}
          </h2>
        </div>
        <AttachedContentTypeCommentsProvider
          key={personId}
          contentTypeKey="custom_survey_answer"
          objectIds={answerIds}
        >
          <CustomSurveyResponsesViewer
            participation={participation}
            respondent={location.state?.person}
          />
        </AttachedContentTypeCommentsProvider>
      </>
    </Page>
  );
};

export default CustomSurveyParticipantResponsePage;
